import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFormatCurrency } from "@/context/CountryContext";
import { UtensilsCrossed, MapPin, Phone, Clock, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const PRICE_LABEL = { economique: "Économique", moyen: "Moyen", premium: "Premium" };
const PRICE_COLOR = { economique: "bg-green-100 text-green-700", moyen: "bg-amber-100 text-amber-700", premium: "bg-purple-100 text-purple-700" };

export default function Restaurants() {
  const fmt = useFormatCurrency();
  const [restos, setRestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [menus, setMenus] = useState([]);

  useEffect(() => {
    api.get("/restaurants").then((r) => setRestos(r.data)).finally(() => setLoading(false));
  }, []);

  const openResto = async (r) => {
    setSelected(r);
    const res = await api.get(`/restaurants/${r.restaurant_id}`);
    setMenus(res.data.menus || []);
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Chargement…</div>;

  if (selected) return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button onClick={() => setSelected(null)} className="text-sm text-primary mb-4 hover:underline">← Retour</button>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="h-40 bg-secondary flex items-center justify-center">
          <UtensilsCrossed className="w-16 h-16 text-white/30" />
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-2xl font-bold text-secondary">{selected.name}</h1>
              <p className="text-muted-foreground text-sm mt-1">{selected.cuisine}</p>
            </div>
            <div className="flex items-center gap-2">
              {selected.price_range && <Badge className={PRICE_COLOR[selected.price_range] || "bg-muted"}>{PRICE_LABEL[selected.price_range]}</Badge>}
              {selected.rating > 0 && <div className="flex items-center gap-1 text-amber-500 font-semibold text-sm"><Star className="w-4 h-4 fill-amber-400" />{selected.rating}</div>}
            </div>
          </div>
          <p className="mt-3 text-muted-foreground">{selected.description}</p>
          <div className="mt-4 space-y-2 text-sm text-muted-foreground">
            {selected.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" />{selected.address}</div>}
            {selected.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 shrink-0" />{selected.phone}</div>}
            {selected.hours && <div className="flex items-center gap-2"><Clock className="w-4 h-4 shrink-0" />{selected.hours}</div>}
          </div>
        </div>
      </div>

      {menus.length > 0 && (
        <div className="mt-6">
          <h2 className="font-display text-lg font-bold text-secondary mb-3">Menu</h2>
          <div className="space-y-2">
            {menus.map((m) => (
              <div key={m.menu_id} className="bg-card border border-border rounded-xl p-4 flex justify-between items-center">
                <div>
                  <div className="font-medium text-secondary">{m.name}</div>
                  <div className="text-sm text-muted-foreground">{m.description}</div>
                  {m.category && <Badge variant="secondary" className="text-xs mt-1">{m.category}</Badge>}
                </div>
                <div className="font-display font-bold text-primary text-lg shrink-0 ml-4">
                  {fmt(m.price)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary flex items-center gap-2">
          <UtensilsCrossed className="w-7 h-7 text-primary" /> Restaurants
        </h1>
        <p className="text-muted-foreground mt-1">Comparez les menus et prix des restaurants à Abidjan.</p>
      </div>

      {restos.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">Aucun restaurant disponible pour l'instant.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {restos.map((r) => (
            <div key={r.restaurant_id} onClick={() => openResto(r)}
              className="bg-card border border-border rounded-2xl overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/30 transition-all">
              <div className="h-36 bg-secondary/10 flex items-center justify-center">
                <UtensilsCrossed className="w-10 h-10 text-secondary/20" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-semibold text-secondary leading-tight">{r.name}</h3>
                  {r.rating > 0 && (
                    <div className="flex items-center gap-0.5 text-amber-500 text-xs font-semibold shrink-0">
                      <Star className="w-3.5 h-3.5 fill-amber-400" />{r.rating}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.cuisine}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{r.city}</div>
                  {r.price_range && <Badge className={`text-xs ${PRICE_COLOR[r.price_range] || "bg-muted"}`}>{PRICE_LABEL[r.price_range]}</Badge>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
